<template>
  <div>
    <h4>{{ $t('current_and_pending_routes') }}:</h4>
    <div class="d-flex justify-center align-center mt-n3">
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
    </div>
    <v-data-table
      class="vf-table"
      hide-default-footer
      :loading="remoteData.isBusy"
      :items="remoteData.items"
      :headers="tableFields"
      :page="remoteData.currentPage"
      :items-per-page="remoteData.rowsPerPage"
      :sort-by.sync="remoteData.sortBy"
      :sort-desc.sync="remoteData.sortDesc">
      <template #item.name="row">
        <router-link :to="`/route/${row.item.id}`">
          <v-icon color="primary" class="pb-1">mdi-road-variant</v-icon>
          <span class="ml-1">{{ row.value }}</span>
        </router-link>
      </template>
      <template #item.vehicle="row">
        <router-link :to="`/vehicle/${row.value.id}`">
          <v-icon color="primary">mdi-truck</v-icon>
          <span class="ml-1">{{ row.value.name }}</span>
        </router-link>
      </template>
      <template #item.start="row">
        <multi-time-display v-if="row.value" :time="row.value" />
        <span v-else class="not-set">{{ $t('not_set') }}</span>
      </template>
      <template #item.end="row">
        <if-set :value="row.value" dash>
          <multi-time-display :time="row.value" />
          <v-chip v-if="routeActive(row.item)" color="success" class="ml-3" small>{{ $t('active') }}</v-chip>
          <template #not-set>
            <span v-if="!row.item.start" class="not-set">{{ $t('not_set') }}</span>
            <span v-else class="not-set">({{ $t('in_progress') }})</span>
          </template>
        </if-set>
      </template>
      <template #item.duration="row">
        <span v-if="row.item.duration">{{ row.item.duration | humanizeDuration({ numberOfSignificantParts: 2, separator: ', ' }) }}</span>
        <span v-else class="not-set">{{ $t('not_set') }}</span>
      </template>
      <template #item.coolerProducts="row">
        <div v-for="routeProduct in row.item.zone_indexed_route_products['cooler']" :key="routeProduct.id">
          <router-link :to="`/product/update/${routeProduct.product.id}`">
            <span>{{ routeProduct.product.name }}</span>
          </router-link>
        </div>
      </template>
      <template #item.freezerProducts="row">
        <div v-for="routeProduct in row.item.zone_indexed_route_products['freezer']" :key="routeProduct.id">
          <router-link :to="`/product/update/${routeProduct.product.id}`">
            <span>{{ routeProduct.product.name }}</span>
          </router-link>
        </div>
      </template>
      <template #item.actions="row">
        <div class="route-buttons ml-n2">
          <v-btn :to="`/route/${row.item.id}`" fab text x-small color="primary">
            <v-icon :to="`/route/${row.item.id}`" small>mdi-eye</v-icon>
          </v-btn>
          <v-btn :to="`/route/update/${row.item.id}`" fab text x-small color="primary">
            <v-icon :to="`/route/${row.item.id}`" small>mdi-pencil</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>

import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import IfSet from '@/components/shared/IfSet'
import Moment from 'moment'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'

export default {
  name: 'CurrentRoutes',
  components: {
    MultiTimeDisplay,
    TablePaginationControls,
    IfSet
  },
  mixins: [
    CurrentUserMixin,
    DateTimeUtilsMixin,
    RemoteDataTableMixin
  ],
  data () {
    return {
      currentRoutes: [],
      model: 'Route',
      sortBy: 'start',
      sortDesc: true,
      tableFields: [
        {
          value: 'name',
          text: this.$tc('route'),
          sortable: true
        },
        {
          value: 'vehicle',
          text: this.$tc('vehicle'),
          sortable: false
        },
        {
          value: 'start',
          text: this.$t('route_start'),
          sortable: true
        },
        {
          value: 'end',
          text: this.$i18n.t('route_end'),
          sortable: true
        },
        {
          value: 'duration',
          text: this.$t('route_duration'),
          sortable: false
        },
        {
          value: 'coolerProducts',
          text: this.$t('cooler_products'),
          sortable: false
        },
        {
          value: 'freezerProducts',
          text: this.$t('freezer_products'),
          sortable: false
        },
        {
          value: 'actions',
          text: this.$tc('action', 2),
          sortable: false
        }
      ]
    }
  },
  methods: {
    buildSearchCriteria () {
      const searchCriteria = {
        current_routes: true,
        expand: 'vehicle,route_products,route_products.product',
        // 'fields': 'id,customer.name,type,visible,created,updated',
        page: this.currentPage,
        'per-page': this.rowsPerPage
      }

      if (this.sortBy !== null) {
        const sortDirection = this.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.sortBy}`
      }
      return searchCriteria
    },
    routeActive (route) {
      const now = Moment()
      return Moment(route.start).isBefore(now) && Moment(route.end).isAfter(now)
    }
  },
  mounted () {
    this.initiateSearch()
  }
}
</script>

<style lang="scss" scoped>
.not-set {
  color: #9e9e9e;
  font-style: italic;
  text-transform: capitalize;
  white-space: nowrap;
}
</style>
